import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Save, User, Mail, Phone, MapPin, Building2, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '@context/AuthContext';
import Button from '@components/common/Button';
import { Badge } from '@components/common/StatusBadge';

const UserProfile = () => {
  const { user, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    department: user?.department || ''
  });
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  // Update profile mutation
  const updateProfileMutation = useMutation(
    updateProfile,
    {
      onSuccess: () => {
        toast.success('Profile updated successfully');
        queryClient.invalidateQueries('user-profile');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update profile');
      }
    }
  );

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      updateProfileMutation.mutate(formData);
    }
  };

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Profile Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
            <User className="w-10 h-10 text-gray-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {user?.first_name} {user?.last_name}
            </h2>
            <div className="flex items-center gap-4 mt-2">
              <Badge variant="primary" size="sm">
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </Badge>
              <Badge variant={user?.is_active ? 'success' : 'default'} size="sm">
                {user?.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                <span>{user?.email}</span>
              </div>
              {user?.department && (
                <div className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  <span>{user?.department}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>
                                    Joined {new Date(user?.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long'
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Form */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Edit Profile</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name *
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.first_name ? 'border-error-300' : 'border-gray-300'
                }`}
                placeholder="Enter first name"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-error-600">{errors.first_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name *
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.last_name ? 'border-error-300' : 'border-gray-300'
                }`}
                placeholder="Enter last name"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-error-600">{errors.last_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.email ? 'border-error-300' : 'border-gray-300'
                }`}
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.phone ? 'border-error-300' : 'border-gray-300'
                }`}
                placeholder="+91-9876543210"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-error-600">{errors.phone}</p>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Department
              </label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter department"
              />
            </div>

            {/* Role (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Role
              </label>
              <input
                type="text"
                value={user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                                Contact your administrator to change your role
              </p>
            </div>
          </div>

          {/* Branch Information (if applicable) */}
          {user?.branch && (
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-base font-medium text-gray-900 mb-4">Branch Information</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="font-medium text-gray-700">Branch Name</label>
                    <p className="text-gray-900">{user.branch.name}</p>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700">Branch Code</label>
                    <p className="text-gray-900">{user.branch.code}</p>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700">Location</label>
                    <p className="text-gray-900">{user.branch.city}, {user.branch.state}</p>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700">Contact</label>
                    <p className="text-gray-900">{user.branch.phone}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account Information */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">Account Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <label className="font-medium text-gray-700">Account Created</label>
                <p className="text-gray-900">
                  {new Date(user?.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Last Updated</label>
                <p className="text-gray-900">
                  {new Date(user?.updated_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Last Login</label>
                <p className="text-gray-900">
                  {user?.last_login ? new Date(user.last_login).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <Button
              type="submit"
              leftIcon={<Save className="w-4 h-4" />}
              loading={updateProfileMutation.isLoading}
              disabled={updateProfileMutation.isLoading}
            >
                            Save Changes
            </Button>
          </div>
        </form>
      </div>

      {/* Activity Summary */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 mb-1">
              {user?.stats?.total_tickets || 0}
            </div>
            <div className="text-sm text-gray-600">Total Tickets</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {user?.stats?.open_tickets || 0}
            </div>
            <div className="text-sm text-gray-600">Open Tickets</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {user?.stats?.resolved_tickets || 0}
            </div>
            <div className="text-sm text-gray-600">Resolved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {user?.stats?.avg_resolution_time || 0}h
            </div>
            <div className="text-sm text-gray-600">Avg Resolution</div>
          </div>
        </div>
      </div>

      {/* Permissions (if user has any) */}
      {user?.permissions && user.permissions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Permissions</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user.permissions.map((permission, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-2">
                  {permission.module?.name || 'General'}
                  {permission.branch && (
                    <span className="text-sm text-gray-500 ml-2">
                                            ({permission.branch.name})
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {permission.can_create && (
                    <Badge variant="success" size="sm">Create</Badge>
                  )}
                  {permission.can_view && (
                    <Badge variant="info" size="sm">View</Badge>
                  )}
                  {permission.can_edit && (
                    <Badge variant="warning" size="sm">Edit</Badge>
                  )}
                  {permission.can_delete && (
                    <Badge variant="error" size="sm">Delete</Badge>
                  )}
                  {permission.can_assign && (
                    <Badge variant="primary" size="sm">Assign</Badge>
                  )}
                  {permission.can_close && (
                    <Badge variant="default" size="sm">Close</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default UserProfile;